import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { Box, MissingRates, eur, eurUnit, num } from './costsFormat';

/**
 * «¿Y si se marcan N llamadas al día?». Escala lo medido en los últimos 30
 * días. NO promete cobros: si llamar más trae más pagos solo lo dirá un
 * experimento con grupo de control (docs/costes-plan.md, «Fuera de este plan»).
 */
const ROWS = [
  ['daily_volume', 'Marcaciones al día', num],
  ['answered_per_day', 'Contestadas al día', num],
  ['useful_per_day', 'Conversaciones útiles al día (≥ 45 s)', num],
  ['human_per_day', 'Con persona al día', num],
  ['talk_hours_per_day', 'Horas hablando al día', num],
  ['telnyx_month_eur', 'Telnyx al mes', eur],
  ['people_per_useful_eur', 'Coste del gestor por conversación útil', eurUnit],
  ['expedients_needed_month', 'Expedientes distintos que hacen falta al mes', num],
];

const CostsSimulator = () => {
  const [volume, setVolume] = useState(800);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async e => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/costs/simulate', { daily_volume: Number(volume) });
      setResult(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='space-y-4'>
      <Box title='Simulador de llamadas'>
        <form onSubmit={run} className='flex flex-wrap items-end gap-3'>
          <label className='text-sm text-slate-600'>
            Marcaciones al día
            <input
              type='number'
              min={1}
              max={10000}
              value={volume}
              onChange={e => setVolume(e.target.value)}
              className='block mt-1 border border-slate-300 rounded-lg px-3 py-1.5 w-40'
            />
          </label>
          <button type='submit' disabled={busy || !volume} className='bg-indigo-600 text-white rounded-lg px-4 py-2 text-sm disabled:opacity-50'>
            {busy ? 'Calculando…' : 'Calcular'}
          </button>
        </form>
        <p className='text-xs text-slate-500 mt-3'>
          Escala lo medido en los últimos 30 días: cuántas contestan, cuánto duran y cuántas veces se llama a cada expediente. No dice si traerá más
          pagos: eso solo lo sabremos con un grupo de control.
        </p>
      </Box>

      {result && !result.enough_data && (
        <div className='bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600'>No hay llamadas en los últimos 30 días para medir.</div>
      )}

      {result?.enough_data && (
        <>
          <MissingRates missing={result.missing} />
          <Box title='Hoy contra lo simulado'>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='text-xs text-slate-500 text-left'>
                  <tr>
                    <th className='px-3 py-2 font-medium' />
                    <th className='px-3 py-2 font-medium text-right'>Hoy (media)</th>
                    <th className='px-3 py-2 font-medium text-right'>Simulado</th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([key, label, fmt]) => (
                    <tr key={key} className='border-t border-slate-100'>
                      <td className='px-3 py-2'>{label}</td>
                      <td className='px-3 py-2 text-right whitespace-nowrap'>{fmt(result.current[key])}</td>
                      <td className='px-3 py-2 text-right whitespace-nowrap font-medium' data-testid={`sim-${key}`}>
                        {fmt(result.projected[key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className='text-xs text-slate-500 mt-3 space-y-1 list-disc list-inside'>
              <li>
                Muestra: {num(result.sample.dials)} marcaciones en {num(result.sample.days)} días, a {num(result.sample.expedients)} expedientes (
                {num(result.sample.dials_per_expedient)} por expediente). Cada marcación cuesta {eurUnit(result.sample.cost_per_dial_eur)} en Telnyx.
              </li>
              <li>
                El gestor cuesta {eur(result.people_month_eur)} al mes haga las llamadas que haga: más llamadas con la misma persona abaratan cada
                conversación. «Horas hablando» no cuenta la espera entre llamadas.
              </li>
              <li>Al mes son {num(result.working_days_per_month)} días laborables.</li>
            </ul>
          </Box>
        </>
      )}
    </div>
  );
};

export default CostsSimulator;
